import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import {
  listResignations,
  approveResignation,
  rejectResignation,
} from "../../api/resignation";
import {
  getResignationsForRole,
  sanitizeResignationForViewer,
  getResignStatusLabel,
  mapApiResignationForReview,
  RESIGN_STATUS,
  canViewResignReason,
  canUpdateResignStatus,
  isPendingResignationStatus,
} from "../../utils/resignHelpers";
import ResignationStatusModal from "./ResignationStatusModal";
import AttachmentList from "./AttachmentList";
import "./ResignationReview.css";

export default function ResignationReview() {
  const { user } = useAuth();
  const { resignations, updateResignations, getEmployeeById } = useData();
  const [apiResignations, setApiResignations] = useState([]);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [statusModal, setStatusModal] = useState({ open: false, resignation: null });
  const [submitting, setSubmitting] = useState(false);

  const canSeeReason = canViewResignReason(user?.role);
  const canUpdateStatus = canUpdateResignStatus(user?.role);

  const loadResignations = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listResignations();
      const list = Array.isArray(data) ? data : data?.items || data?.data || [];
      const mapped = list.map(mapApiResignationForReview).filter(Boolean);
      setApiResignations(mapped);
      updateResignations(mapped);
      setApiLoaded(true);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "تعذر تحميل طلبات الاستقالة",
      );
      setApiResignations([]);
      setApiLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [updateResignations]);

  useEffect(() => {
    loadResignations();
  }, [loadResignations]);

  const visibleResignations = useMemo(() => {
    // Backend already scopes the list to hierarchy + HR/GM; use it as source of truth.
    if (apiLoaded) {
      return apiResignations.map((r) =>
        sanitizeResignationForViewer(r, user?.role),
      );
    }

    return getResignationsForRole(
      resignations,
      user?.role,
      user?.id,
      getEmployeeById,
      user?.employeeId,
    ).map((r) => sanitizeResignationForViewer(r, user?.role));
  }, [
    apiLoaded,
    apiResignations,
    resignations,
    user?.role,
    user?.id,
    user?.employeeId,
    getEmployeeById,
  ]);


  const filtered = useMemo(() => {
    if (filter === "pending") {
      return visibleResignations.filter((r) =>
        isPendingResignationStatus(r.status),
      );
    }
    if (filter === "closed") {
      return visibleResignations.filter(
        (r) =>
          r.status === RESIGN_STATUS.WITHDRAWN ||
          r.status === RESIGN_STATUS.APPROVED ||
          r.status === RESIGN_STATUS.REJECTED,
      );
    }
    return visibleResignations;
  }, [visibleResignations, filter]);

  const pendingCount = visibleResignations.filter((r) =>
    isPendingResignationStatus(r.status),
  ).length;

  function openStatusModal(resignation) {
    setStatusModal({ open: true, resignation });
  }

  function closeStatusModal() {
    setStatusModal({ open: false, resignation: null });
  }

  async function handleStatusUpdate({ status, notes, lastWorkingDay }) {
    const resignation = statusModal.resignation;
    if (!resignation) return;

    setSubmitting(true);
    setError("");
    try {
      if (status === RESIGN_STATUS.APPROVED) {
        await approveResignation(resignation.id, {
          notes: notes || undefined,
          ...(lastWorkingDay ? { lastWorkingDay } : {}),
        });
      } else if (status === RESIGN_STATUS.REJECTED) {
        await rejectResignation(resignation.id, { notes: notes || undefined });
      }

      const now = new Date().toISOString();
      updateResignations((prev) =>
        prev.map((r) =>
          r.id === resignation.id
            ? {
                ...r,
                status,
                reviewedBy: user.name,
                reviewedByRole: user.role,
                reviewedAt: now,
                reviewNotes: notes || null,
                updatedAt: now,
              }
            : r,
        ),
      );

      await loadResignations();
      closeStatusModal();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "تعذر تحديث حالة طلب الاستقالة",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="resignation-review" dir="rtl">
      <div className="ess-home-header">
        <h1 className="ess-home-title">طلبات الاستقالة</h1>
        <p className="ess-home-subtitle">
          {canUpdateStatus
            ? "متابعة طلبات الاستقالة والموافقة أو الرفض — القرار النهائي للموارد البشرية فقط"
            : "عرض طلبات الاستقالة (اطلاع فقط — الموافقة والرفض من صلاحيات الموارد البشرية)"}
        </p>
      </div>

      {error && <p className="resign-error">{error}</p>}

      <div className="resignation-review-filters">
        <button
          type="button"
          className={`resignation-filter-btn ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          الكل ({visibleResignations.length})
        </button>
        <button
          type="button"
          className={`resignation-filter-btn ${filter === "pending" ? "active" : ""}`}
          onClick={() => setFilter("pending")}
        >
          قيد المراجعة ({pendingCount})
        </button>
        <button
          type="button"
          className={`resignation-filter-btn ${filter === "closed" ? "active" : ""}`}
          onClick={() => setFilter("closed")}
        >
          منتهية
        </button>
      </div>

      {loading ? (
        <div className="ess-table-card">
          <p className="ess-empty-message">جارٍ تحميل طلبات الاستقالة...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="ess-table-card">
          <p className="ess-empty-message">لا توجد طلبات استقالة</p>
        </div>
      ) : (
        filtered.map((r) => (
          <div key={r.id} className="resignation-review-card">
            <div className="resignation-review-header">
              <div>
                <h3 className="resignation-review-name">{r.employeeName}</h3>
                {r.workplaceLabel ? (
                  <p className="resignation-review-workplace">
                    {r.workplaceLabel}
                  </p>
                ) : null}
                <p className="resignation-review-meta">
                  تاريخ التقديم:{" "}
                  {new Date(r.submittedAt).toLocaleDateString("ar-EG")}
                </p>
              </div>
              <span
                className={`resignation-review-status status-${r.status}`}
              >
                {getResignStatusLabel(r.status)}
              </span>
            </div>

            <div className="resignation-review-details">
              <div className="resignation-review-row">
                <span className="resignation-review-label">آخر يوم عمل:</span>
                <span>
                  {new Date(r.lastWorkingDay).toLocaleDateString("ar-EG")}
                </span>
              </div>

              {canSeeReason && r.reason && (
                <div className="resignation-review-reason">
                  <span className="resignation-review-label">سبب الاستقالة:</span>
                  <p>{r.reason}</p>
                </div>
              )}

              {r.handoverNotes && (
                <div className="resignation-review-row">
                  <span className="resignation-review-label">
                    ملاحظات التسليم:
                  </span>
                  <span>{r.handoverNotes}</span>
                </div>
              )}

              {r.fileNames?.length > 0 && (
                <div className="resignation-review-row">
                  <span className="resignation-review-label">المستندات:</span>
                  <span>{r.fileNames.join("، ")}</span>
                </div>
              )}

              {r.id && (
                <AttachmentList
                  entityType="resignation"
                  entityId={r.id}
                  showDelete={false}
                  compact
                />
              )}

              {r.reviewedBy && (
                <div className="resignation-review-review-info">
                  <span className="resignation-review-label">
                    تمت المراجعة بواسطة:
                  </span>
                  <span>
                    {r.reviewedBy} —{" "}
                    {new Date(r.reviewedAt).toLocaleDateString("ar-EG")}
                  </span>
                  {r.reviewNotes && (
                    <p className="resignation-review-notes">{r.reviewNotes}</p>
                  )}
                </div>
              )}
            </div>

            {canUpdateStatus && isPendingResignationStatus(r.status) && (
              <div className="resignation-review-actions">
                <button
                  type="button"
                  className="resignation-review-action-btn"
                  onClick={() => openStatusModal(r)}
                  disabled={submitting}
                >
                  تغيير الحالة
                </button>
              </div>
            )}
          </div>
        ))
      )}

      <ResignationStatusModal
        isOpen={statusModal.open}
        onClose={closeStatusModal}
        onSubmit={handleStatusUpdate}
        resignation={statusModal.resignation}
      />
    </div>
  );
}
